import type { ZoneEquipmentSignal } from "@/lib/api/agent";
import { formatSignalNumericValue } from "@/lib/format-signal-value";

// zone_receives_as (connection alias) → bare equipment data_point_history.name.
// Same last-segment rule as resolve_equipment_publishes (heating-coil.supply_air_temp
// → supply_air_temp). Kept explicit so a new alias cannot silently guess wrong.
const EQUIPMENT_POINT_FOR_ALIAS: Record<string, string> = {
  ahu_extract_air_temp: "extract_air_temp",
  ahu_extract_air_co2: "extract_air_co2",
  ahu_supply_air_temp: "supply_air_temp",
  ahu_supply_airflow: "supply_air_flow",
  people_counter_headcount: "headcount",
  co2_sensor_co2: "co2",
  temperature_sensor_temperature: "temperature",
};

/** Maps a connection alias to the equipment history point name Signaalit / Syöte use. */
export function equipmentHistoryName(zone_receives_as: string): string {
  return EQUIPMENT_POINT_FOR_ALIAS[zone_receives_as] ?? zone_receives_as;
}

export interface ZoneSyoteListRow {
  zone_id: string;
  zone_name: string;
  name: string;
  unit: string;
  value: string;
  recorded_at: string | undefined;
  connection_source: string;
  connection_source_name: string;
  connection_source_id: string;
  connection_upstream_name: string;
  // The zone-native data_point id this Syöte row substitutes for (the compare signal's own
  // `zone_data_point_id`), or null when this publish names no zone-native channel at all (e.g.
  // extract-air CO₂, which the zone has no native co2 counterpart to substitute). Task #1766:
  // the seam that routes this row through @/lib/zone-kpi.ts's shared reader when it happens to
  // be one of the three zone-KPI channels (occupancy_count / co2 / temperature).
  native_id: string | null;
}

function lookup(
  by_equipment: Map<string, Record<string, string>>,
  equipment_id: string,
  point_id: string,
): string | undefined {
  const by_name = by_equipment.get(equipment_id);
  if (!by_name) return undefined;
  if (by_name[point_id]) return by_name[point_id];
  const sibling = Object.entries(by_name).find(([name]) =>
    name.startsWith(`${point_id}__`),
  );
  return sibling?.[1];
}

export function compareCoveredNativeIds(
  signals: ZoneEquipmentSignal[],
): Set<string> {
  const substituted = new Set(
    signals
      .filter((s) => s.relation === "substitute" && s.zone_data_point_id)
      .map((s) => s.zone_data_point_id as string),
  );
  return new Set(
    signals
      .filter(
        (s) =>
          s.relation === "compare" &&
          s.zone_data_point_id &&
          !substituted.has(s.zone_data_point_id),
      )
      .map((s) => s.zone_data_point_id as string),
  );
}

export function zoneCompareHistoryNames(
  signals: ZoneEquipmentSignal[],
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const signal of signals) {
    if (signal.relation !== "compare") continue;
    const name = equipmentHistoryName(signal.zone_receives_as);
    if (seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

// Downstream-zone Syöte rows — same chrome as AHU chilled_water_supply_temp
// (name, ← upstream instance, Syöte chip). Compare publishes only; substitutes
// stay the zone's own Anturitieto channel.
export function buildZoneSyoteListRows(
  zones: { source_id: string; source_name: string }[],
  signals_by_zone: Record<string, ZoneEquipmentSignal[]>,
  equipment_name_by_id: Map<string, string>,
  equipment_type_by_id: Map<string, string>,
  units_by_equipment: Map<string, Record<string, string>>,
): ZoneSyoteListRow[] {
  const rows: ZoneSyoteListRow[] = [];
  for (const zone of zones) {
    for (const signal of signals_by_zone[zone.source_id] ?? []) {
      if (signal.relation !== "compare") continue;
      if (signal.equipment_value == null) continue;
      const point_id = equipmentHistoryName(signal.zone_receives_as);
      const unit =
        lookup(units_by_equipment, signal.equipment_id, point_id) ?? "";
      const source_name =
        equipment_name_by_id.get(signal.equipment_id) ?? signal.equipment_type;
      rows.push({
        zone_id: zone.source_id,
        zone_name: zone.source_name,
        name: point_id,
        unit,
        value: formatSignalNumericValue(
          signal.equipment_value,
          unit || undefined,
        ),
        recorded_at: signal.equipment_recorded_at ?? undefined,
        connection_source:
          equipment_type_by_id.get(signal.equipment_id) ??
          signal.equipment_type,
        connection_source_name: source_name,
        connection_source_id: signal.equipment_id,
        connection_upstream_name: point_id,
        native_id: signal.zone_data_point_id,
      });
    }
  }
  return rows;
}
